import React, { useState } from "react";
import {
  Github,
  Globe2,
  Instagram,
  Linkedin,
  Newspaper,
  Twitter,
  X,
} from "lucide-react";
import { SiPocket } from "react-icons/si";
import { useTheme } from "../ThemeContext";
import { useSiteContent } from "../SiteContentContext";

const socialIcons = {
  github: Github,
  crumbles: Globe2,
  instagram: Instagram,
  linkedin: Linkedin,
  medium: Newspaper,
  twitter: Twitter,
};

function SocialBubble({ platform, href, isDark, isNeo, onSelect }) {
  const Icon = socialIcons[platform];
  const label = platform.charAt(0).toUpperCase() + platform.slice(1);

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Open ${label} profile`}
      title={label}
      onClick={onSelect}
      className={`floating-social-link flex h-9 w-9 flex-none items-center justify-center rounded-full transition-all duration-200 hover:-translate-y-0.5 ${
        isNeo
          ? "bg-white text-black hover:bg-[#FF4FD8]"
          : isDark
            ? "text-gray-300 hover:bg-white hover:text-black"
            : "text-gray-700 hover:bg-gray-950 hover:text-white"
      }`}
    >
      <Icon size={16} strokeWidth={2.2} />
    </a>
  );
}

export default function FloatingSocials() {
  const { isDark, isNeo } = useTheme();
  const { content } = useSiteContent();
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const socials = (content.footer?.socials || []).filter(
    ({ platform, href }) => socialIcons[platform] && href
  );

  if (socials.length === 0) return null;

  const dockClass = isNeo
    ? "border-[3px] border-black bg-[#FFE600] shadow-[5px_5px_0_#000]"
    : isDark
      ? "border border-white/10 bg-[#15151d]/90 shadow-[0_14px_40px_rgba(0,0,0,0.35)]"
      : "border border-black/10 bg-white/90 shadow-[0_14px_40px_rgba(35,28,20,0.14)]";

  return (
    <aside
      aria-label="Social links"
      className="fixed right-3 top-24 z-40 md:right-5"
    >
      <div
        className={`floating-social-dock flex flex-col items-center rounded-full p-1.5 backdrop-blur-xl md:hidden ${dockClass}`}
      >
        <button
          type="button"
          aria-label={isMobileOpen ? "Collapse social links" : "Expand social links"}
          aria-controls="mobile-social-links"
          aria-expanded={isMobileOpen}
          onClick={() => setIsMobileOpen((open) => !open)}
          className={`floating-social-toggle flex h-9 w-9 flex-none items-center justify-center rounded-full transition-colors ${
            isNeo
              ? "bg-white text-black hover:bg-[#FF4FD8]"
              : isDark
                ? "text-white hover:bg-white/10"
                : "text-gray-900 hover:bg-black/5"
          }`}
        >
          {isMobileOpen ? (
            <X size={17} />
          ) : (
            <SiPocket
              aria-hidden="true"
              size={18}
            />
          )}
        </button>

        <div
          id="mobile-social-links"
          aria-hidden={!isMobileOpen}
          className={`flex flex-col items-center gap-1.5 overflow-hidden transition-all duration-300 ease-out ${
            isMobileOpen
              ? "mt-1.5 max-h-[15.5rem] opacity-100"
              : "pointer-events-none max-h-0 opacity-0"
          }`}
        >
          {socials.map(({ platform, href }) => (
            <SocialBubble
              key={platform}
              platform={platform}
              href={href}
              isDark={isDark}
              isNeo={isNeo}
              onSelect={() => setIsMobileOpen(false)}
            />
          ))}
        </div>
      </div>

      <div
        className={`floating-social-dock hidden flex-col items-center gap-1.5 rounded-full p-1.5 backdrop-blur-xl md:flex ${dockClass}`}
      >
        {socials.map(({ platform, href }) => (
          <SocialBubble
            key={platform}
            platform={platform}
            href={href}
            isDark={isDark}
            isNeo={isNeo}
          />
        ))}
      </div>
    </aside>
  );
}
